/**
 * Presentational dropzone for the Brand Bible file intake (BB-FILE-DROP-01,
 * slice B-FE). Purely visual + interaction: drag-over/click-to-browse, an
 * accept filter, and per-file state rows. It does NOT upload — the caller owns
 * the transport (BbFileDropCard, wired to the pinned contract Appendix B.2
 * shapes) and drives each file's state through `files`. Mounted from
 * BrandBiblePage's "Import files" toggle, replacing the former
 * "Import .docx — coming soon" stub.
 *
 * State vocabulary matches Carl's B-FE-2 addendum mapping (bb_files.status →
 * FE label): stored→queued, parsing→reading, parsed→done, failed→error.
 * `duplicate` is upload-time only (B.2 `status:"duplicate"`, no bb_files.status
 * equivalent — the file already exists, nothing new was stored).
 */
import { useRef, useState } from 'react';
import type { DragEvent } from 'react';
import { CheckCircle2, Copy, FileText, Loader2, Upload, XCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

export type DropFileState =
  | 'stored'
  | 'rejected'
  | 'unconfirmed'
  | 'queued'
  | 'uploading'
  | 'reading'
  | 'done'
  | 'empty'
  | 'error'
  | 'duplicate';

export interface DropFileRow {
  /** Caller-stable id (e.g. `${name}:${size}`). */
  id: string;
  name: string;
  state: DropFileState;
  /** Human-readable detail — server error verbatim, or "12 drafts" on done. */
  detail?: string;
  /** Caller sets this when a read attempt discovered the reader is disabled
   * (503) — offers a manual retry without pretending auto-read will differ. */
  showReadButton?: boolean;
  /** Server file id — present once upload stored the row. Used by Review →. */
  fileId?: string;
}

const ACCEPT_EXT = ['.docx', '.pdf', '.txt', '.md'];

export const isAcceptedFile = (name: string): boolean =>
  ACCEPT_EXT.some((ext) => name.toLowerCase().endsWith(ext));

const STATE_LABEL: Record<DropFileState, string> = {
  stored: 'Stored',
  rejected: 'Rejected',
  unconfirmed: 'Not confirmed',
  queued: 'Queued',
  uploading: 'Uploading…',
  reading: 'Reading…',
  done: 'Done',
  empty: 'Could not use this',
  error: 'Failed',
  duplicate: 'Already on file',
};

export function FileDropZone({
  files,
  disabled,
  onFiles,
  onReadFile,
  onReviewFile,
}: {
  files: DropFileRow[];
  disabled?: boolean;
  /** Receives only accepted-extension files; rejected names go to onFiles' second arg. */
  onFiles: (accepted: File[], rejectedNames: string[]) => void;
  /** Forwards a manual "Read" click for a row with `showReadButton` — the
   * component owns no transport, so the caller drives the retry itself. */
  onReadFile?: (id: string) => void;
  /** Per-file Review → — only for done rows that actually drafted. */
  onReviewFile?: (fileId: string) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);

  const handle = (list: FileList | null) => {
    if (!list || disabled) return;
    const all = Array.from(list);
    const accepted = all.filter((f) => isAcceptedFile(f.name));
    const rejected = all.filter((f) => !isAcceptedFile(f.name)).map((f) => f.name);
    if (accepted.length || rejected.length) onFiles(accepted, rejected);
  };

  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    handle(e.dataTransfer?.files ?? null);
  };

  return (
    <div className="flex flex-col gap-2">
      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-label="Drop your files here or browse"
        aria-disabled={disabled || undefined}
        data-testid="bb-file-dropzone"
        onClick={() => !disabled && inputRef.current?.click()}
        onKeyDown={(e) => {
          if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            inputRef.current?.click();
          }
        }}
        onDragOver={(e) => {
          e.preventDefault();
          if (!disabled) setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        className={cn(
          'flex flex-col items-center gap-1.5 rounded-lg border-2 border-dashed px-6 py-7 text-center transition-colors',
          disabled
            ? 'cursor-not-allowed border-border opacity-50'
            : dragOver
              ? 'cursor-pointer border-coral bg-coral-light'
              : 'cursor-pointer border-input bg-warm-bg hover:border-coral',
        )}
      >
        <Upload className={cn('size-5', dragOver ? 'text-coral' : 'text-muted-foreground')} />
        <div className="text-[13.5px] font-medium">Drop your files here</div>
        <div className="text-[11.5px] text-muted-foreground">
          or click to browse · {ACCEPT_EXT.join(' ')} · up to 10 files, 15 MiB each
        </div>
        <input
          ref={inputRef}
          type="file"
          multiple
          disabled={disabled}
          accept={ACCEPT_EXT.join(',')}
          className="hidden"
          data-testid="bb-file-input"
          onChange={(e) => {
            handle(e.target.files);
            e.target.value = '';
          }}
        />
      </div>

      {files.length > 0 && (
        <ul className="flex flex-col gap-1" data-testid="bb-file-rows" aria-live="polite">
          {files.map((f) => (
            <li
              key={f.id}
              className="flex items-center gap-2.5 rounded-lg border border-border bg-card px-3 py-2"
            >
              <FileText className="size-3.5 flex-none text-muted-foreground" />
              <span className="min-w-0 flex-1 truncate text-[12.5px]">{f.name}</span>
              {f.detail && (
                <span
                  className={cn(
                    'truncate text-[11px]',
                    (f.state === 'error' || f.state === 'unconfirmed') ? 'text-destructive' : 'text-muted-foreground',
                  )}
                >
                  {f.detail}
                </span>
              )}
              {f.showReadButton && onReadFile && (
                <button
                  type="button"
                  onClick={() => onReadFile(f.id)}
                  className="flex-none text-[11px] font-semibold text-coral hover:text-coral-hover"
                >
                  Read
                </button>
              )}
              {f.state === 'done' && f.fileId && onReviewFile && (
                <button
                  type="button"
                  onClick={() => onReviewFile(f.fileId!)}
                  className="flex-none text-[11px] font-semibold text-coral hover:text-coral-hover"
                >
                  Review →
                </button>
              )}
              <span
                className={cn(
                  'flex flex-none items-center gap-1 text-[11px] font-semibold',
                  (f.state === 'done' || f.state === 'stored') && 'text-coral-dark',
                  (f.state === 'error' || f.state === 'rejected' || f.state === 'unconfirmed') && 'text-destructive',
                  (f.state === 'uploading' || f.state === 'reading') && 'text-teal-dark',
                  (f.state === 'queued' || f.state === 'duplicate' || f.state === 'empty') &&
                    'text-muted-foreground',
                )}
              >
                {(f.state === 'uploading' || f.state === 'reading') && (
                  <Loader2 className="size-3 animate-spin" />
                )}
                {(f.state === 'done' || f.state === 'stored') && <CheckCircle2 className="size-3" />}
                {(f.state === 'error' || f.state === 'rejected' || f.state === 'unconfirmed') && <XCircle className="size-3" />}
                {f.state === 'duplicate' && <Copy className="size-3" />}
                {STATE_LABEL[f.state]}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
